import * as React from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import EditLessonForm from './fillInBlankLessonForm';

import {
  FillInBlankForm,
  Modal,
  ALPHA_FLAG,
} from '../../../Shared/index';
import { ActionTypes } from '../../actions/actionTypes';
import * as lessonActions from '../../actions/grammarActivities';
import * as fillInBlankQuestionsActions from '../../actions/fillInBlankQuestions';
import { GrammarActivity } from '../../interfaces/grammarActivities';
import { Match } from '../../interfaces/match';
import { Question, Questions } from '../../interfaces/questions';
import { ConceptReducerState } from '../../reducers/conceptsReducer';
import { FillInBlankConceptsFeedbackState } from '../../reducers/fillInBlankConceptsFeedbackReducer';
import { GrammarActivityState } from '../../reducers/grammarActivitiesReducer';

interface FillInBlankActivityProps {
  concepts: ConceptReducerState;
  dispatch: Function;
  fillInBlank: Questions;
  fillInBlankConceptsFeedback: FillInBlankConceptsFeedbackState;
  isSidebar?: boolean;
  lessons: GrammarActivityState;
  match: Match;
}

const FillInBlankActivity: React.FC<FillInBlankActivityProps> = ({
  concepts,
  dispatch,
  fillInBlank,
  fillInBlankConceptsFeedback,
  isSidebar = false,
  lessons,
  match,
}) => {
  const [newQuestion, setNewQuestion] = React.useState(false);

  const { lessonID } = match.params;
  const { data, hasreceiveddata, states } = lessons;

  const lesson = lessonID && data ? data[lessonID] : null;

  const cancelEditingLesson = () => {
    if (lessonID) {
      dispatch(lessonActions.cancelLessonEdit(lessonID));
    }
  };

  const editLesson = () => {
    if (lessonID) {
      dispatch(lessonActions.startLessonEdit(lessonID));
    }
  };

  const createQuestion = () => {
    setNewQuestion(true);
  };

  const cancelCreateQuestion = () => {
    setNewQuestion(false);
  };

  const submitNewQuestion = (questionObj: Question) => {
    if (lessonID) {
      dispatch(fillInBlankQuestionsActions.submitNewQuestion(questionObj, lessonID));
      setNewQuestion(false);
    }
  };

  const saveLessonEdits = (vals: GrammarActivity) => {
    if (lessonID) {
      // Ensure fill_in_the_blank stays true
      const updatedVals = { ...vals, fill_in_the_blank: true };
      dispatch(lessonActions.submitLessonEdit(lessonID, updatedVals));
    }
  };

  const renderEditLessonForm = () => {
    if (lessonID && lesson && states && states[lessonID] === ActionTypes.EDITING_LESSON) {
      return (
        <Modal close={cancelEditingLesson}>
          <EditLessonForm
            concepts={concepts}
            conceptsFeedback={fillInBlankConceptsFeedback}
            currentValues={lesson}
            fillInBlank={fillInBlank}
            lesson={lesson}
            submit={saveLessonEdits}
          />
        </Modal>
      );
    }
  };

  const renderCreateQuestionModal = () => {
    if (!newQuestion || !lessonID || !lesson) return;

    const formState = {
      flag: lesson.flag || ALPHA_FLAG
    };

    return (
      <Modal close={cancelCreateQuestion}>
        <FillInBlankForm
          action={submitNewQuestion}
          isGrammar={true}
          new={true}
          showOptimalResponse={false}
          state={formState}
        />
      </Modal>
    );
  };

  const renderQuestionsAsList = (questionsForLesson: Question[]) => {
    return questionsForLesson.map((question: Question, index: number) => {
      const { cues, key, prompt } = question;
      const { questionID } = match.params;
      const className = key === questionID ? "selected question-box" : "question-box";
      const linkUrl = `/admin/activity-view/${lessonID}/fill-in-the-blanks/${key}/responses`;

      return (
        <li className={className} key={key}>
          <Link to={linkUrl}>
            <p>
              <span>{index + 1}.&nbsp;</span>
              <span dangerouslySetInnerHTML={{ __html: prompt }} />
            </p>
            {cues?.length && (
              <div>
                {cues.map((cue, cueIndex) => (
                  <span className="cue" key={`cue-${key}-${cueIndex}`}>{cue}</span>
                ))}
              </div>
            )}
          </Link>
        </li>
      );
    });
  };

  const renderQuestionsForLesson = () => {
    const lessonQuestions = lesson ? lesson.questions : null;

    if (fillInBlank.hasreceiveddata) {
      if (lessonQuestions && lessonQuestions.length) {
        const questionsForLesson = lessonQuestions
          .map(q => {
            const question = fillInBlank.data[q.key];
            if (!question) return null;
            return { ...question, key: q.key };
          })
          .filter(Boolean);
        return <ul>{renderQuestionsAsList(questionsForLesson)}</ul>;
      }
      return <p>No questions</p>;
    }
    return <div>Loading...</div>;
  };

  if (data && lessonID && data[lessonID]) {
    const activityFlag = data[lessonID].flag;
    const numberOfQuestions = lesson?.questions ? lesson.questions.length : 0;

    if (isSidebar) {
      return (
        <div className="edit-activity">
          <Link to={`/admin/fitb_activities/${lessonID}`}>Back</Link>
          <br /><br />
          {renderEditLessonForm()}
          <h4 className="title">{data[lessonID].title || data[lessonID].name}</h4>
          <h6 className="subtitle">{data[lessonID].flag}</h6>
          <p className="control">
            <button className="button" onClick={editLesson} type="button">Edit Activity</button>
          </p>
          <h6 className="subtitle">{numberOfQuestions} Questions</h6>
          {renderQuestionsForLesson()}
        </div>
      );
    }

    return (
      <div className="fill-in-blank-activity-manager">
        <Link to={`/admin/fitb_activities?flag=${activityFlag}`}>Return to {activityFlag} activities</Link>
        <br />
        {renderEditLessonForm()}
        {renderCreateQuestionModal()}
        <h1 className="title">{data[lessonID].title || data[lessonID].name}</h1>
        <h2 className="subtitle">{data[lessonID].flag}</h2>
        <div className="buttons-wrapper">
          <button className="quill-button small contained green focus-on-light" onClick={editLesson} type="button">Edit Activity</button>
          <Link className="quill-button small contained green focus-on-light" rel="noopener noreferrer" target="_blank" to={`/play/sw?anonymous=true&uid=${lessonID}`}>Play Activity</Link>
          <button className="quill-button small contained green focus-on-light" onClick={createQuestion} type="button">Create Question</button>
        </div>
        {renderQuestionsForLesson()}
      </div>
    );
  }

  if (hasreceiveddata === false) {
    return <p>Loading...</p>;
  }

  return <p>404: No Activity Found</p>;
};

function select(state: any) {
  return {
    concepts: state.concepts,
    fillInBlank: state.fillInBlank,
    fillInBlankConceptsFeedback: state.fillInBlankConceptsFeedback,
    lessons: state.grammarActivities,
  };
}

export default connect(select)(FillInBlankActivity);
